import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { baseQueryWithReauth } from '../main/baseQueryWithReauth';
import type { RootState } from '../index';
import type {
  StepResponse,
  RegisterResponse,
  SessionResponse,
  RegistrationData,
  OnboardingArtifact,
} from './models';
import { apiBaseUrl } from 'lib.shared/src/api';

const ONBOARDING_TAG = 'onboarding';

/**
 * Raw base query without reauth — used for pre-auth onboarding steps.
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
});

/**
 * Custom baseQuery for onboarding:
 * - Pre-auth steps: use raw fetch (no 401 redirect)
 * - Post-auth steps: use baseQueryWithReauth (normal auth flow)
 */
const onboardingBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const state = api.getState() as RootState;
  const isAuthenticated = Boolean(state?.userAuth?.user?.id);

  if (isAuthenticated) {
    return baseQueryWithReauth(args, api, extraOptions);
  }

  return rawBaseQuery(args, api, extraOptions);
};

export const onboardingApi = createApi({
  reducerPath: 'onboarding',
  baseQuery: onboardingBaseQuery,
  tagTypes: [ONBOARDING_TAG],
  endpoints: (build) => ({
    getCurrentStep: build.query<StepResponse, { hash: string }>({
      query: ({ hash }) => ({
        url: `/onboarding/${hash}/current-step`,
        headers: { Accept: 'application/json' },
      }),
      providesTags: [{ type: ONBOARDING_TAG, id: 'CURRENT_STEP' }],
    }),

    submitStep: build.mutation<StepResponse, { hash: string; data: Record<string, unknown> }>({
      query: ({ hash, data }) => ({
        url: `/onboarding/${hash}/submit`,
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: ONBOARDING_TAG, id: 'CURRENT_STEP' }],
    }),

    goBack: build.mutation<StepResponse, { hash: string }>({
      query: ({ hash }) => ({
        url: `/onboarding/${hash}/back`,
        method: 'POST',
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: ONBOARDING_TAG, id: 'CURRENT_STEP' }],
    }),

    saveStrategyProgress: build.mutation<
      StepResponse,
      {
        hash: string;
        data: {
          reach_topics?: string[];
          engagement_hooks?: Record<string, string>;
          leadgen_notes?: string;
        };
      }
    >({
      query: ({ hash, data }) => ({
        url: `/onboarding/${hash}/strategy`,
        method: 'PATCH',
        body: data,
        headers: { Accept: 'application/json' },
      }),
      // no cache invalidation — keep current render stable; next full refetch
      // will pick up the persisted state. Intended for silent background saves.
    }),

    registerFromOnboarding: build.mutation<
      RegisterResponse,
      { hash: string; data: RegistrationData }
    >({
      query: ({ hash, data }) => ({
        url: `/onboarding/${hash}/register`,
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      }),
      invalidatesTags: [{ type: ONBOARDING_TAG, id: 'CURRENT_STEP' }],
    }),

    getSession: build.query<SessionResponse, { hash: string }>({
      query: ({ hash }) => ({
        url: `/onboarding/${hash}/session`,
        headers: { Accept: 'application/json' },
      }),
    }),

    getInviteArtifact: build.query<
      { data: OnboardingArtifact },
      { hash: string; artifactId: number }
    >({
      query: ({ hash, artifactId }) => ({
        url: `/onboarding/${hash}/invite/artifact/${artifactId}`,
        headers: { Accept: 'application/json' },
      }),
    }),
  }),
});

export const {
  useGetCurrentStepQuery,
  useSubmitStepMutation,
  useGoBackMutation,
  useSaveStrategyProgressMutation,
  useRegisterFromOnboardingMutation,
  useGetSessionQuery,
  useGetInviteArtifactQuery,
} = onboardingApi;
